import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, HandHelping, CalendarDays, Users, Megaphone, HandCoins, Drum, Sun, Ticket } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import DonateButton from "@/components/DonateButton";
import NewsletterForm from "@/components/NewsletterForm";
import { TICKETS_URL } from "@/components/AnnouncementBar";
import { INVOLVE_CARDS, SUPPORT_GOES, WELLNESS_NOTE, WELLNESS_URL, IMG } from "@/lib/site";

const ICONS = { heart: Heart, hand: HandHelping, calendar: CalendarDays, users: Users, megaphone: Megaphone, coins: HandCoins };

export default function GetInvolved() {
  return (
    <>
      <section className="bg-charcoal pb-16 pt-40 text-ivory lg:pb-24 lg:pt-52" data-testid="involved-hero">
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6 text-gold">Get Involved</p>
            <h1 className="max-w-3xl text-balance font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="involved-headline">
              There are many ways to create change.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ivory/80">
              This is not simply a charity asking for support. It is a movement inviting you to participate — donate, attend, heal, dance, volunteer, partner, share.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO PARTICIPATE */}
      <section className="bg-ivory py-24 lg:py-32" data-testid="involve-cards-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="involve-cards-grid">
            {INVOLVE_CARDS.map((card, i) => {
              const Icon = ICONS[card.icon];
              return (
                <Reveal key={card.title} delay={i * 0.06}>
                  <div className="flex h-full flex-col rounded-2xl border border-hairline bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`involve-card-${i + 1}`}>
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-gold">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-display text-2xl font-semibold text-forest">{card.title}</h3>
                    <p className="mt-2.5 flex-1 leading-relaxed text-mutedink">{card.desc}</p>
                    {card.title === "Donate" ? (
                      <DonateButton variant="outlineDark" className="mt-7 w-full" testid="involve-donate-button" />
                    ) : (
                      <Link to="/contact" data-testid={`involve-cta-${i + 1}`} className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-forest/40 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-forest hover:text-ivory">
                        Reach Out <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DONATION SECTION */}
      <section className="bg-sand py-24 lg:py-32" data-testid="donation-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Donate"
                title="Help create change for Congo."
                sub="Your support helps Nseya Foundation work toward clean water, medical care, safe spaces, community resources, sustainable development and our long-term Women & Children's Sanctuary vision."
              />
              <Reveal delay={0.1}>
                <div className="mt-8">
                  <DonateButton variant="big" testid="involved-main-donate-button" />
                </div>
                <div className="mt-10 rounded-2xl border-l-4 border-gold bg-white p-6" data-testid="involved-wellness-note">
                  <p className="text-sm leading-relaxed text-ink">
                    <strong className="font-semibold">50% of proceeds</strong> from the Nseya Wellness Retreat go directly to the Nseya Humanitarian Foundation. When you invest in your wellness, you invest in others. Learn more at{" "}
                    <a href={WELLNESS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline decoration-gold underline-offset-4">www.nseyawellness.com</a>
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <p className="eyebrow mb-8 text-ochre">Where your support goes</p>
                <div className="grid gap-4 sm:grid-cols-2" data-testid="support-goes-grid">
                  {SUPPORT_GOES.map((s, i) => (
                    <div key={s.title} className="rounded-xl border border-hairline bg-white p-6" data-testid={`support-goes-${i + 1}`}>
                      <h3 className="font-display text-lg font-semibold text-forest">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-mutedink">{s.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm italic leading-relaxed text-mutedink" data-testid="support-transparency-note">
                  Specific project budgets, timelines and fundraising targets will be published as projects are developed and verified. We do not make claims we cannot support.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WELLNESS */}
      <section id="wellness" className="scroll-mt-24 bg-ivory py-24 lg:py-32" data-testid="involved-wellness-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHeading
                eyebrow="Wellness"
                title="What if wellness could create change?"
                sub="Nseya wellness workshops and retreats create spaces for people to slow down, reconnect, move, breathe, learn and restore. Participation helps raise funds for Nseya Foundation's humanitarian work and long-term sanctuary vision."
              />
              <Reveal delay={0.1}>
                <p className="mt-5 font-display text-2xl italic text-forest">You come to heal. You leave having helped build something.</p>
                <a href={WELLNESS_URL} target="_blank" rel="noopener noreferrer" data-testid="involved-wellness-cta" className="mt-8 inline-flex items-center gap-2 rounded-full border border-forest/40 px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-forest hover:text-ivory">
                  Visit Nseya Wellness <ArrowUpRight className="h-4 w-4" />
                </a>
                <div className="mt-8 rounded-2xl border-l-4 border-gold bg-sand p-6">
                  <p className="text-sm leading-relaxed text-ink">{WELLNESS_NOTE.split("Learn more")[0]}Learn more at{" "}
                    <a href={WELLNESS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline decoration-gold underline-offset-4">www.nseyawellness.com</a>
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.15}>
                <img src="/brand/dance-event-group.jpg" alt="Guests dancing in a circle at a Nseya wellness workshop" className="aspect-[3/2] w-full rounded-2xl object-cover" loading="lazy" data-testid="wellness-image-1" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* DANCE FOR CONGO */}
      <section id="dance" className="relative scroll-mt-24 overflow-hidden bg-earth py-24 text-ivory lg:py-32" data-testid="dance-page-section">
        <img src={IMG.drum} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-earth via-earth/85 to-earth/50" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHeading dark eyebrow="Culture · Events" title="Dance for Congo" sub="Culture is connection." />
              <Reveal delay={0.1}>
                <div className="mt-6 max-w-2xl space-y-5 text-base leading-relaxed text-ivory/85 sm:text-lg" data-testid="dance-description">
                  <p>
                    Dance for Congo is a cultural celebration rooted in truth, rhythm, and ancestral medicine. The world uses Congo — its minerals power our technology, its rainforest breathes for our planet — yet few know that Congo is also the birthplace of some of the world's most beloved dance forms. Semba, Samba, Salsa, Rhumba and so many more trace their roots back to Congo. We have forgotten to dance. And dancing is medicine.
                  </p>
                  <p>
                    Dance for Congo brings people together to move, learn, and remember. Through ancestral dance traditions we reconnect with our bodies, our history, and each other. Each event introduces participants to different dance styles rooted in Congolese culture — because movement is healing, and Congo has always known this.
                  </p>
                  <p>
                    The Congolese people find beauty in everything. In their music, their fashion, their rhythm, their resilience. Dance for Congo honours that spirit.
                  </p>
                  <p>
                    Funds raised support the Nseya Humanitarian Foundation's initiatives on the ground in Congo. We have already had 2 successful events. The next Dance for Congo event is coming. Subscribe below to be the first to know.
                  </p>
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                  <a href={TICKETS_URL} target="_blank" rel="noopener noreferrer" data-testid="dance-tickets-button" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-300 hover:bg-golddark">
                    <Ticket className="h-4 w-4" /> Get Your Tickets
                  </a>
                  <DonateButton variant="outlineLight" testid="dance-page-donate-button" />
                </div>
              </Reveal>
            </div>
            <div className="hidden lg:col-span-5 lg:block">
              <Reveal delay={0.15}>
                <div className="space-y-4">
                  {[
                    { icon: Drum, label: "Music & rhythm" },
                    { icon: Users, label: "Community & storytelling" },
                    { icon: Sun, label: "Fundraising for change" },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-4 rounded-xl border border-ivory/20 bg-charcoal/40 p-5 backdrop-blur">
                      <Icon className="h-5 w-5 text-gold" />
                      <p className="text-sm uppercase tracking-[0.18em]">{label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15}>
            <p className="eyebrow mt-16 text-gold/90">From our past events</p>
            <div className="mt-6 grid gap-4 md:grid-cols-12" data-testid="dance-gallery">
              <div className="group overflow-hidden rounded-2xl ring-1 ring-ivory/15 md:col-span-12">
                <img
                  src="/brand/dance-for-congo.jpg"
                  alt="The Dance for Congo community posing together after a workshop"
                  className="aspect-[5/2] w-full object-cover [object-position:center_25%] transition-transform duration-700 group-hover:scale-[1.02]"
                  loading="lazy"
                  data-testid="dance-gallery-photo-1"
                />
              </div>
              {[
                ["/brand/dance-event-2.jpg", "A guest smiling behind the conga drums at a Dance for Congo event"],
                ["/brand/dance-event-3.jpg", "A facilitator speaking to the group at a Dance for Congo workshop"],
                ["/brand/dance-event-4.jpg", "A dance instructor leading the class"],
              ].map(([src, alt], i) => (
                <div key={src} className="group overflow-hidden rounded-2xl ring-1 ring-ivory/15 md:col-span-4">
                  <img
                    src={src}
                    alt={alt}
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    data-testid={`dance-gallery-photo-${i + 2}`}
                  />
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-14 max-w-2xl rounded-2xl border border-ivory/20 bg-charcoal/50 p-8 backdrop-blur" data-testid="dance-event-signup">
              <p className="eyebrow text-gold">Next event updates</p>
              <p className="mt-3 text-sm leading-relaxed text-ivory/75">
                Be the first to know when the next Dance for Congo is announced.
              </p>
              <div className="mt-6">
                <NewsletterForm dark idPrefix="dance-signup" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
